import { memo, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Drawer,
    Stack,
    TextField,
    FormControlLabel,
    Switch,
    Slider,
    Button,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useGetBrandsQuery } from 'src/context/api/competitorsMonitoring/api';
import { ComponentBlockCustom } from 'src/sections/mui/component-block-custom';
import { setFilterAveragePositionPLA, setFilterBrandPLA, setFilterPricingRangePLA, setNotificationBrandInfringement } from 'src/context/reducers/filter-settings/filterSlice';

type Props = {
    open: boolean;
    onClose: () => void;
};

function AdsFiltersDrawer({ open, onClose }: Props) {
    const dispatch = useDispatch();
    const filterStore = useSelector((state: any) => state.filters);
    const workspace: any = useCurrentWorkspace();

    const { data: brands } = useGetBrandsQuery({ bqTable: workspace?.bqTable } as any, { skip: !workspace?.bqTable });


    // Optimize slider updates: use local state during drag, commit to store on release
    const [localAveragePosition, setLocalAveragePosition] = useState<[number, number]>(
        () => (filterStore?.averagePositionPLA as [number, number]) || [0, 50]
    );

    useEffect(() => {
        const valueFromStore = (filterStore?.averagePositionPLA as [number, number]) || [0, 50];
        setLocalAveragePosition(valueFromStore);
    }, [filterStore?.averagePositionPLA]);

    const handlePositionChange = useCallback((_e: any, newValue: number | number[]) => {
        dispatch(setFilterAveragePositionPLA(newValue as [number, number]));
    }, [dispatch]);

    const handlePositionCommit = useCallback(() => {
        // Position is already committed in handlePositionChange
    }, []);

    const positionMarks = useMemo(
        () => Array.from({ length: 11 }, (_, i) => ({ value: i * 10, label: `${i * 10}` })),
        []
    );

    const handleResetFilters = useCallback(() => {
        // Reset store values
        dispatch(setFilterBrandPLA([]));
        dispatch(setFilterPricingRangePLA([0, 50000]));
        dispatch(setFilterAveragePositionPLA([0, 50]));
        dispatch(setNotificationBrandInfringement(false));

        // Sync local component state
        setPricingFromLocal(0);
        setPricingToLocal(50000);
    }, [dispatch]);


    const handleBrandInfringementChange = (event: any) => {
        const isChecked = event?.target?.checked;
        if (isChecked) {
            // Active: on stocke un marqueur spécial pour signifier "filtrer sur brand infringement"
            dispatch(setFilterBrandPLA(['__BRAND_INFRINGEMENT__'] as any));
        } else {
            dispatch(setFilterBrandPLA([]));
            dispatch(setNotificationBrandInfringement(true));
        }
    };

    const pricingFrom = filterStore?.pricingRangePLA?.[0] ?? 0;
    const pricingTo = filterStore?.pricingRangePLA?.[1] ?? 50000;

    // Price inputs: local state to avoid dispatching on every keystroke
    const [pricingFromLocal, setPricingFromLocal] = useState<number>(pricingFrom);
    const [pricingToLocal, setPricingToLocal] = useState<number>(pricingTo);

    useEffect(() => {
        setPricingFromLocal(pricingFrom);
        setPricingToLocal(pricingTo);
    }, [pricingFrom, pricingTo]);

    const commitPricingRange = useCallback(() => {
        const safeFrom = Number.isFinite(pricingFromLocal) ? pricingFromLocal : 0;
        const safeTo = Number.isFinite(pricingToLocal) ? pricingToLocal : 50000;
        dispatch(setFilterPricingRangePLA([safeFrom, safeTo]));
    }, [dispatch, pricingFromLocal, pricingToLocal]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: 320, display: 'flex', flexDirection: 'column', height: '100%' } }}
        >
            <Stack spacing={3} px={2} pt={2}>
                {workspace?.showPlaAlert && (
                    <ComponentBlockCustom title="Brand Protection">
                        <FormControlLabel
                            sx={{ p: 1.5, mt: .5 }}
                            control={
                                <Switch
                                    checked={filterStore?.brandsPLA?.length !== 0}
                                    onChange={handleBrandInfringementChange}
                                />
                            }
                            label="Brand Infringement"
                        />
                    </ComponentBlockCustom>
                )}

                <ComponentBlockCustom sx={{ pt: 3, px: 2 }} title="Price">
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                            size="small"
                            fullWidth
                            type="number"
                            label='From'
                            value={pricingFromLocal}
                            onChange={(e) => setPricingFromLocal(Number(e.target.value))}
                            onBlur={commitPricingRange}
                            onKeyDown={(e) => { if (e.key === 'Enter') commitPricingRange(); }}
                        />
                        <TextField
                            size="small"
                            fullWidth
                            type="number"
                            label='To'
                            value={pricingToLocal}
                            onChange={(e) => setPricingToLocal(Number(e.target.value))}
                            onBlur={commitPricingRange}
                            onKeyDown={(e) => { if (e.key === 'Enter') commitPricingRange(); }}
                        />
                    </Box>
                </ComponentBlockCustom>

                <ComponentBlockCustom sx={{ pt: 3, px: 4, mb: 1 }} title="Position">
                    <Slider
                        value={localAveragePosition}
                        onChange={handlePositionChange}
                        onChangeCommitted={handlePositionCommit}
                        valueLabelDisplay="on"
                        min={0}
                        sx={{ mt: 3 }}
                        max={15}
                        marks={positionMarks}
                        step={1}
                        disableSwap
                    />
                </ComponentBlockCustom>
            </Stack>
            <Box sx={{ px: 2, py: 2, mt: 'auto' }}>
                <Button fullWidth variant="outlined" color="inherit" onClick={handleResetFilters}>
                    Reset filters
                </Button>
            </Box>
        </Drawer>
    );
}

export default memo(AdsFiltersDrawer);




